/**
 * PIFster Vote Intent API
 *
 * Strict TypeScript conversion for the magic-link / vote intent client API.
 */

import { AuthIntentPurpose, AuthIntent, MagicLinkStatus } from '../types/pifster-contracts';

type PifsterWindow = Window & {
  PIFSTER_BOOTSTRAP?: PifsterBootstrap | null;
  PIFSTER_DEBUG?: boolean;
  PIFster?: PifsterNamespace;
};

interface PifsterBootstrap {
  readonly apiBase?: string;
  readonly loginUrl?: string;
  readonly debug?: boolean;
  readonly demoEnabled?: boolean;
  readonly demoAliases?: readonly string[];
}

interface PifsterNamespace {
  readonly apiBase?: string;
  readonly debug?: boolean;
}

interface MagicLinkPayload {
  email: string;
  redirect_to: string;
  charity_id?: number;
  period_id?: number;
  turnstile_token?: string;
}

interface MagicLinkInput {
  readonly email: string;
  readonly charity_id?: number | undefined;
  readonly period_id?: number | undefined;
  readonly turnstile_token?: string | undefined;
}

export interface MagicLinkResponse {
  readonly status?: MagicLinkStatus;
  readonly intent?: AuthIntent;
}

function dlog(...args: unknown[]): void {
  try {
    const pifsterWindow = window as PifsterWindow;
    const enabled = pifsterWindow.PIFSTER_DEBUG === true || pifsterWindow.PIFster?.debug === true;
    if (!enabled) {
      return;
    }

    if (window.console && typeof window.console.debug === 'function') {
      window.console.debug('[PIF vote][api]', ...args);
    }
  } catch {
    // no-op
  }
}

export function resolveApiBase(): string {
  try {
    const pifsterWindow = window as PifsterWindow;
    const configuredBase = pifsterWindow.PIFster?.apiBase ?? pifsterWindow.PIFSTER_BOOTSTRAP?.apiBase ?? '';
    const desiredBase = new URL('/wp-json/pifster/v1', window.location.origin).href.replace(/\/$/, '');
    return desiredBase || String(configuredBase).replace(/\/$/, '');
  } catch {
    return '';
  }
}

export function sendMagicLink(payload: MagicLinkInput): Promise<MagicLinkResponse> {
  const currentUrl = window.location.href.split('#').shift() ?? window.location.href;
  const charityId = typeof payload.charity_id === 'number' && Number.isFinite(payload.charity_id) && payload.charity_id > 0
    ? Math.trunc(payload.charity_id)
    : undefined;
  const periodId = typeof payload.period_id === 'number' && Number.isFinite(payload.period_id) && payload.period_id > 0
    ? Math.trunc(payload.period_id)
    : undefined;
  const turnstileToken = typeof payload.turnstile_token === 'string' && payload.turnstile_token.trim() !== ''
    ? payload.turnstile_token.trim()
    : undefined;
  const normalized: MagicLinkPayload = {
    redirect_to: currentUrl,
    email: String(payload.email).trim().toLowerCase(),
  };

  if (charityId !== undefined) {
    normalized.charity_id = charityId;
  }

  if (periodId !== undefined) {
    normalized.period_id = periodId;
  }

  if (turnstileToken !== undefined) {
    normalized.turnstile_token = turnstileToken;
  }

  if (!normalized.email || normalized.email.indexOf('@') === -1) {
    return Promise.reject(new Error('A valid email is required.'));
  }

  const base = resolveApiBase();
  const bootstrap = (window as PifsterWindow).PIFSTER_BOOTSTRAP;
  const demoAliases = Array.isArray(bootstrap?.demoAliases)
    ? bootstrap.demoAliases.map((alias) => String(alias).trim().toLowerCase())
    : [];
  const isDemoAlias = bootstrap?.demoEnabled === true && demoAliases.includes(normalized.email);
  const fallbackUrl = `${window.location.origin}/?rest_route=/pifster/v1/auth/intent`;

  const request = (url: string): Promise<Response> => {
    return fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(normalized),
    });
  };

  const responseError = async (response: Response): Promise<Error> => {
    try {
      const body = (await response.json()) as { readonly message?: unknown };
      if (typeof body.message === 'string' && body.message.trim() !== '') {
        return new Error(body.message);
      }
    } catch {
      // Use the status fallback when the response has no JSON error body.
    }

    return new Error(`Request failed (HTTP ${response.status})`);
  };

  const handleResponse = (response: Response): Promise<MagicLinkResponse> => {
    if (!response.ok) {
      throw { retry: response.status !== 400 && response.status !== 422, status: response.status };
    }

    return response.json() as Promise<MagicLinkResponse>;
  };

  const handleFallback = (err: unknown): Promise<MagicLinkResponse> => {
    const fallbackError = err as { retry?: boolean; status?: number } | undefined;
    if (!fallbackError || !fallbackError.retry) {
      throw new Error(fallbackError?.status ? `Request failed (HTTP ${fallbackError.status})` : 'Request failed.');
    }

    dlog('sendMagicLink retrying with fallback route');
    return request(fallbackUrl).then((resp: Response) => {
      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}`);
      }

      return resp.json() as Promise<MagicLinkResponse>;
    });
  };

  if (isDemoAlias) {
    if (!base) {
      return Promise.reject(new Error('Demo API base is unavailable.'));
    }

    return request(`${base}/demo/magic-link`).then(async (response: Response) => {
      if (!response.ok) {
        throw await responseError(response);
      }

      return response.json() as Promise<MagicLinkResponse>;
    });
  }

  if (!base) {
    dlog('base API missing, using fallback route only');
    return request(fallbackUrl).then(handleResponse);
  }

  return request(`${base}/auth/intent`)
    .then(handleResponse)
    .catch(handleFallback)
    .then((data: MagicLinkResponse) => {
      dlog('sendMagicLink success');
      return data;
    });
}

export {};
